import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { dashboardFinancieroCorpService, consolidacionDeptService } from '../../services/financialService';


const PERIODS = ['Hoy', 'Semana', 'Mes', 'Trimestre', 'Año'];
const PERIOD_MAP = { 'Hoy': 'today', 'Semana': 'week', 'Mes': 'month', 'Trimestre': 'quarter', 'Año': 'year' };
const DEPTS = ['Todos', 'Tienda', 'Sala Corte', 'Distribución', 'Corporativo'];

const fmt = (n) => (typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00');
const fmtEur = (n) => `${fmt(n)} €`;
const fmtPct = (n) => `${(typeof n === 'number' ? n : 0)?.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const SimuladorPanel = ({ breakEven }) => {
  const [sim, setSim] = useState({ ventasDelta: 0, costosFijosDelta: 0, costosVariablesDelta: 0, margenDelta: 0 });

  if (!breakEven) return null;

  const ventasSimuladas = (breakEven?.facturacionTotal || 0) * (1 + sim?.ventasDelta / 100);
  const costosFijosSimulados = (breakEven?.costosFijos || 0) + sim?.costosFijosDelta;
  const costosVariablesSimulados = (breakEven?.costosVariables || 0) * (1 + sim?.costosVariablesDelta / 100);
  const margenContribPct = sim?.margenDelta !== 0
    ? Math.max(0, (breakEven?.margenContribucionPct || 0) + sim?.margenDelta) / 100
    : ventasSimuladas > 0 ? (ventasSimuladas - costosVariablesSimulados) / ventasSimuladas : 0;
  const nuevoPuntoEquilibrio = margenContribPct > 0 ? costosFijosSimulados / margenContribPct : 0;
  const nuevoResultado = ventasSimuladas - costosFijosSimulados - costosVariablesSimulados;
  const nuevoMargen = ventasSimuladas > 0 ? (nuevoResultado / ventasSimuladas) * 100 : 0;

  const sliders = [
    { key: 'ventasDelta', label: 'Ventas', unit: '%', min: -50, max: 100, color: '#34D399' },
    { key: 'costosFijosDelta', label: 'Costos Fijos', unit: '€', min: -2000, max: 5000, step: 100, color: '#FBBF24' },
    { key: 'costosVariablesDelta', label: 'Costos Variables', unit: '%', min: -30, max: 50, color: '#A78BFA' },
    { key: 'margenDelta', label: 'Margen Contrib.', unit: '%', min: -20, max: 20, color: '#60A5FA' },
  ];

  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Sliders" size={15} className="text-[#D6EF4E]" />
        <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Simulador Financiero</h3>
        <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.20)', color: '#D6EF4E' }}>
          Sin modificar datos reales
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {sliders?.map(s => (
          <div key={s?.key}>
            <div className="flex items-center justify-between mb-1">
              <label className="font-oranienbaum text-white/50 text-xs">{s?.label}</label>
              <span className="font-oranienbaum text-white/70 text-xs font-semibold" style={{ color: s?.color }}>
                {sim?.[s?.key] > 0 ? '+' : ''}{sim?.[s?.key]}{s?.unit}
              </span>
            </div>
            <input type="range" min={s?.min} max={s?.max} step={s?.step || 1} value={sim?.[s?.key]}
              onChange={e => setSim(p => ({ ...p, [s?.key]: Number(e?.target?.value) }))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, ${s?.color} 0%, ${s?.color} ${((sim?.[s?.key] - s?.min) / (s?.max - s?.min)) * 100}%, rgba(255,255,255,0.10) ${((sim?.[s?.key] - s?.min) / (s?.max - s?.min)) * 100}%, rgba(255,255,255,0.10) 100%)` }} />
          </div>
        ))}
      </div>
      <button onClick={() => setSim({ ventasDelta: 0, costosFijosDelta: 0, costosVariablesDelta: 0, margenDelta: 0 })}
        className="text-xs font-oranienbaum text-white/30 hover:text-white/60 transition-colors mb-4">
        Restablecer
      </button>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Nuevo Resultado', value: fmtEur(nuevoResultado), color: nuevoResultado >= 0 ? '#34D399' : '#F87171' },
          { label: 'Nuevo Margen', value: fmtPct(nuevoMargen), color: nuevoMargen >= 0 ? '#D6EF4E' : '#F87171' },
          { label: 'Nuevo Punto Equilibrio', value: fmtEur(nuevoPuntoEquilibrio), color: '#A78BFA' },
          { label: 'Nueva Venta Necesaria/Mes', value: fmtEur(nuevoPuntoEquilibrio), color: '#60A5FA' },
        ]?.map((r, i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${r?.color}20` }}>
            <p className="font-oranienbaum font-bold text-sm" style={{ color: r?.color }}>{r?.value}</p>
            <p className="font-oranienbaum text-white/35 text-[10px] mt-0.5 leading-tight">{r?.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FinanzasCorporativas = () => {
  const [period, setPeriod] = useState('Mes');
  const [dept, setDept] = useState('Todos');
  const [kpis, setKpis] = useState(null);
  const [breakEven, setBreakEven] = useState(null);
  const [comparativa, setComparativa] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [consolidacion, setConsolidacion] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const periodKey = PERIOD_MAP?.[period] || 'month';
      // Pasar departamento real a todos los servicios
      // FIX: siempre cargar consolidación por departamento (no solo cuando dept === 'Todos')
      const [kpisData, beData, compData, alertasData, consolidData] = await Promise.all([
        dashboardFinancieroCorpService?.getKPIs({ period: periodKey, departamento: dept }),
        dashboardFinancieroCorpService?.getBreakEven({ period: periodKey, departamento: dept }),
        dashboardFinancieroCorpService?.getComparativa(dept),
        dashboardFinancieroCorpService?.getAlertas(dept),
        consolidacionDeptService?.getByDepartamento({ period: periodKey }),
      ]);
      setKpis(kpisData);
      setBreakEven(beData);
      setComparativa(compData);
      setAlertas(alertasData);
      setConsolidacion(consolidData || []);
    } catch (err) {
      setError(err?.message || 'Error al cargar dashboard financiero');
    } finally {
      setLoading(false);
    }
  }, [period, dept]);

  useEffect(() => { loadData(); }, [loadData]);

  const kpiCards = [
    { label: 'Facturación Total', value: kpis ? fmtEur(kpis?.facturacionTotal || 0) : 'Pendiente de datos', icon: 'TrendingUp', color: '#D6EF4E' },
    { label: 'Ingresos Cobrados', value: kpis ? fmtEur(kpis?.ingresosCobrados || 0) : 'Pendiente de datos', icon: 'CheckCircle', color: '#34D399' },
    { label: 'Cobros Pendientes', value: kpis ? fmtEur(kpis?.cobrosPendientes || 0) : 'Pendiente de datos', icon: 'Clock', color: '#FBBF24' },
    { label: 'Resultado Operativo', value: kpis ? fmtEur(kpis?.beneficioOperativo || 0) : 'Pendiente de datos', icon: 'Activity', color: '#60A5FA' },
    { label: 'Costes Fijos Norm.', value: kpis ? fmtEur(kpis?.costosFijosNormalizados || 0) : 'Pendiente de datos', icon: 'Lock', color: '#A78BFA' },
    { label: 'Costes Variables', value: kpis ? fmtEur(kpis?.costosVariables || 0) : 'Pendiente de datos', icon: 'TrendingDown', color: '#F87171' },
    { label: 'Margen Contribución', value: kpis ? fmtPct(kpis?.margenContribucion || 0) : 'Pendiente de datos', icon: 'Percent', color: '#34D399' },
    { label: 'Margen Operativo', value: kpis ? fmtPct(kpis?.margenBruto || 0) : 'Pendiente de datos', icon: 'BarChart2', color: '#D6EF4E' },
    { label: 'Tesorería Disponible', value: kpis ? fmtEur(kpis?.tesoreriaDisponible || 0) : 'Pendiente de datos', icon: 'Wallet', color: '#60A5FA' },
    { label: 'Saldo Bancario', value: kpis ? fmtEur(kpis?.saldoBancario || 0) : 'Pendiente de datos', icon: 'Landmark', color: '#D6EF4E' },
    { label: 'Pagos Pendientes', value: kpis ? fmtEur(kpis?.pagosPendientes || 0) : 'Pendiente de datos', icon: 'ArrowUpCircle', color: '#F87171' },
    { label: 'Capital Circulante', value: kpis ? fmtEur(kpis?.capitalCirculante || 0) : 'Pendiente de datos', icon: 'RefreshCw', color: '#A78BFA' },
  ];

  const beStatusColor = breakEven?.estadoProyeccion === 'EN_OBJETIVO' ? '#34D399'
    : breakEven?.estadoProyeccion === 'RIESGO' ? '#FBBF24'
    : breakEven?.estadoProyeccion === 'PENDIENTE_DATOS' ? '#60A5FA' : '#F87171';
  const beStatusLabel = breakEven?.estadoProyeccion === 'EN_OBJETIVO' ? 'En Objetivo'
    : breakEven?.estadoProyeccion === 'RIESGO' ? 'Riesgo'
    : breakEven?.estadoProyeccion === 'PENDIENTE_DATOS' ? 'Pendiente de datos' : 'Por Debajo del Equilibrio';

  const compData = comparativa ? [
    { label: 'Ventas', actual: comparativa?.ventas?.actual || 0, anterior: comparativa?.ventas?.anterior || 0, diff: comparativa?.ventas?.diff || 0, pct: comparativa?.ventas?.pct || 0 },
    { label: 'Costes', actual: comparativa?.costes?.actual || 0, anterior: comparativa?.costes?.anterior || 0, diff: comparativa?.costes?.diff || 0, pct: comparativa?.costes?.pct || 0 },
    { label: 'Beneficio', actual: comparativa?.beneficio?.actual || 0, anterior: comparativa?.beneficio?.anterior || 0, diff: comparativa?.beneficio?.diff || 0, pct: comparativa?.beneficio?.pct || 0 },
    { label: 'Tesorería', actual: comparativa?.tesoreria?.actual || 0, anterior: comparativa?.tesoreria?.anterior || 0, diff: comparativa?.tesoreria?.diff || 0, pct: comparativa?.tesoreria?.pct || 0 },
  ] : [];

  // Mapear datos de consolidación por departamento
  const deptConsolidMap = {};
  (consolidacion || [])?.forEach(d => {
    deptConsolidMap[d?.departamento] = d;
  });

  const deptCards = [
    { label: 'Sala de Corte', emoji: '🔪', color: '#D6EF4E', key: 'SALA_CORTE' },
    { label: 'Distribución', emoji: '🚚', color: '#60A5FA', key: 'DISTRIBUCION' },
    { label: 'Tienda General', emoji: '🏪', color: '#34D399', key: 'TIENDA' },
    { label: 'Corporativo', emoji: '🏢', color: '#A78BFA', key: 'CORPORATIVO' },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-xs font-oranienbaum text-white/30">
        <Icon name="Home" size={12} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span>
        <span className="text-white/50">Dashboard Financiero</span>
      </div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="LayoutDashboard" size={20} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide">Dashboard Financiero</h1>
            <p className="font-oranienbaum text-white/40 text-xs">
              Vista ejecutiva · {dept === 'Todos' ? 'Consolidado' : dept} · {period}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowSimulator(!showSimulator)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-oranienbaum transition-colors"
            style={showSimulator ? { background: '#D6EF4E', color: '#000' } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="Sliders" size={13} />
            Simulador
          </button>
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
            Actualizar
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {PERIODS?.map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${period === p ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
              style={period === p ? { background: '#D6EF4E' } : {}}>
              {p}
            </button>
          ))}
        </div>
        <select value={dept} onChange={e => setDept(e?.target?.value)}
          className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 bg-transparent outline-none cursor-pointer"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {DEPTS?.map(d => <option key={d} value={d} style={{ background: '#111' }}>{d}</option>)}
        </select>
        {dept !== 'Todos' && (
          <span className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.20)', color: '#D6EF4E' }}>
            <Icon name="Filter" size={11} />
            Filtrado: {dept}
          </span>
        )}
      </div>
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-4 flex flex-col gap-2 group hover:scale-[1.01] transition-transform"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
              </div>
            </div>
            <p className="font-oranienbaum font-bold text-white/80 text-lg truncate">
              {loading ? '...' : kpi?.value}
            </p>
            <p className="font-oranienbaum text-white/35 text-[10px] leading-tight">{kpi?.label}</p>
          </div>
        ))}
      </div>
      {/* Break-even section */}
      {(breakEven || loading) && (
        <div className="mb-6 rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Target" size={15} className="text-[#A78BFA]" />
              <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Punto de Equilibrio</h3>
              {breakEven?.diasAperturaMes && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum"
                  style={{ background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(96,165,250,0.20)', color: '#60A5FA' }}>
                  {breakEven?.diasAperturaMes} días apertura/mes
                </span>
              )}
            </div>
            {breakEven && (
              <span className="px-3 py-1 rounded-full text-xs font-oranienbaum font-semibold"
                style={{ background: `${beStatusColor}15`, border: `1px solid ${beStatusColor}30`, color: beStatusColor }}>
                {beStatusLabel}
              </span>
            )}
          </div>
          {breakEven?.estadoProyeccion === 'PENDIENTE_DATOS' ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Icon name="Info" size={20} className="text-[#60A5FA]/50" />
              <p className="font-oranienbaum text-white/40 text-xs text-center">
                Pendiente de datos — Introduce gastos recurrentes y facturas para calcular el punto de equilibrio
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {[
                  { label: 'Costes Fijos', value: fmtEur(breakEven?.costosFijos || 0), color: '#A78BFA' },
                  { label: 'Costes Variables', value: fmtEur(breakEven?.costosVariables || 0), color: '#F87171' },
                  { label: 'Margen Contribución', value: fmtPct(breakEven?.margenContribucionPct || 0), color: '#34D399' },
                  { label: 'Punto Equilibrio', value: fmtEur(breakEven?.puntoEquilibrio || 0), color: '#A78BFA' },
                  { label: 'Facturación Actual', value: fmtEur(breakEven?.facturacionTotal || 0), color: '#34D399' },
                  { label: '% Alcanzado', value: fmtPct(breakEven?.pctAlcanzado || 0), color: beStatusColor },
                  { label: 'Falta hasta Equilibrio', value: fmtEur(breakEven?.faltaHastaEquilibrio || 0), color: (breakEven?.faltaHastaEquilibrio || 0) <= 0 ? '#34D399' : '#F87171' },
                ]?.map((item, i) => (
                  <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item?.color}15` }}>
                    <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color }}>{loading ? '...' : item?.value}</p>
                    <p className="font-oranienbaum text-white/35 text-[9px] mt-0.5 leading-tight">{item?.label}</p>
                  </div>
                ))}
              </div>
              {/* Venta necesaria */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Venta Mensual Necesaria', value: fmtEur(breakEven?.ventaMensual || 0), sub: 'punto de equilibrio mensual', color: '#A78BFA' },
                  { label: 'Venta Semanal Necesaria', value: fmtEur(breakEven?.ventaSemanal || 0), sub: `sobre ${breakEven?.diasAperturaSemana || 6} días/semana`, color: '#60A5FA' },
                  { label: 'Venta Diaria Necesaria', value: fmtEur(breakEven?.ventaDiaria || 0), sub: `sobre ${breakEven?.diasAperturaMes || 26} días apertura`, color: '#D6EF4E' },
                  { label: 'Proyección Fin de Mes', value: fmtEur(breakEven?.proyeccionFinMes || 0), sub: `ritmo actual × ${(breakEven?.diasAbiertosTranscurridos || 0) + (breakEven?.diasAbiertosRestantes || 0)} días abiertos`, color: beStatusColor },
                ]?.map((item, i) => (
                  <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item?.color}15` }}>
                    <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color }}>{loading ? '...' : item?.value}</p>
                    <p className="font-oranienbaum text-white/35 text-[9px] mt-0.5 leading-tight">{item?.label}</p>
                    <p className="font-oranienbaum text-white/20 text-[8px] mt-0.5">{item?.sub}</p>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-oranienbaum text-white/40 text-[10px]">0 €</span>
                  <span className="font-oranienbaum text-white/40 text-[10px]">Punto Equilibrio: {fmtEur(breakEven?.puntoEquilibrio || 0)}</span>
                </div>
                <div className="h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, breakEven?.pctAlcanzado || 0)}%`, background: beStatusColor }} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* Simulator */}
      {showSimulator && <div className="mb-6"><SimuladorPanel breakEven={breakEven} /></div>}
      {/* Comparativa mes actual vs anterior */}
      {compData?.length > 0 && (
        <div className="mb-6 rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="ArrowLeftRight" size={15} className="text-[#60A5FA]" />
            <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Mes Actual vs Mes Anterior</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {compData?.map((item, i) => (
              <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-2">{item?.label}</p>
                <p className="font-oranienbaum font-bold text-white/80 text-sm">{fmtEur(item?.actual)}</p>
                <p className="font-oranienbaum text-white/40 text-[10px]">Anterior: {fmtEur(item?.anterior)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Icon name={item?.diff >= 0 ? 'TrendingUp' : 'TrendingDown'} size={11} style={{ color: item?.diff >= 0 ? '#34D399' : '#F87171' }} />
                  <span className="font-oranienbaum text-[10px] font-semibold" style={{ color: item?.diff >= 0 ? '#34D399' : '#F87171' }}>
                    {item?.diff >= 0 ? '+' : ''}{fmtEur(item?.diff)} ({item?.pct >= 0 ? '+' : ''}{fmtPct(item?.pct)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Alerts & Dept consolidation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={15} className="text-[#FBBF24]" />
              <h3 className="font-oranienbaum font-semibold text-white/80 text-sm">Alertas Financieras</h3>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
              style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', color: '#FBBF24' }}>
              {alertas?.length} alertas
            </span>
          </div>
          {alertas?.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Icon name="CheckCircle" size={24} className="text-[#34D399]/50" />
              <p className="font-oranienbaum text-white/30 text-xs">Sin alertas activas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alertas?.map((alert, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-2">
                    <Icon name={alert?.nivel === 'error' ? 'XCircle' : alert?.nivel === 'info' ? 'Info' : 'AlertTriangle'} size={13}
                      style={{ color: alert?.nivel === 'error' ? '#F87171' : alert?.nivel === 'info' ? '#60A5FA' : '#FBBF24' }} />
                    <span className="font-oranienbaum text-white/55 text-xs">{alert?.mensaje}</span>
                  </div>
                  <span className="font-oranienbaum text-white/30 text-xs font-semibold">{alert?.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dept consolidation — datos reales siempre visibles */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="PieChart" size={15} className="text-[#D6EF4E]" />
            <h3 className="font-oranienbaum font-semibold text-white/80 text-sm">Consolidación por Departamento</h3>
          </div>
          <div className="space-y-3">
            {deptCards?.map((d, i) => {
              const data = deptConsolidMap?.[d?.key];
              const ingresos = data?.ingresos_facturados || 0;
              const costes = (data?.costes_fijos || 0) + (data?.costes_variables || 0);
              const beneficio = data?.beneficio_operativo || 0;
              const margen = data?.margen_contribucion_pct || 0;
              const hasData = ingresos > 0 || costes > 0;

              return (
                <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${d?.color}15` }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{d?.emoji}</span>
                      <span className="font-oranienbaum text-white/70 text-xs font-semibold">{d?.label}</span>
                    </div>
                    <span className="font-oranienbaum text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: `${d?.color}10`, color: d?.color }}>
                      {loading ? '...' : hasData ? `${margen?.toFixed(1)}% margen` : 'Sin datos'}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'Ingresos', value: ingresos, color: '#34D399' },
                      { label: 'Costes', value: costes, color: '#F87171' },
                      { label: 'Resultado', value: beneficio, color: beneficio >= 0 ? '#34D399' : '#F87171' },
                      { label: 'Margen', value: null, pct: margen, color: d?.color },
                    ]?.map((metric, j) => (
                      <div key={j} className="text-center">
                        <p className="font-oranienbaum text-white/25 text-[9px] uppercase tracking-wider">{metric?.label}</p>
                        <p className="font-oranienbaum text-xs font-semibold mt-0.5" style={{ color: metric?.color }}>
                          {loading ? '...' : !hasData ? '—' : metric?.pct !== undefined ? fmtPct(metric?.pct) : fmtEur(metric?.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {hasData && (
                    <div className="mt-2 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-full rounded-full"
                        style={{ width: `${Math.min(100, Math.max(0, margen))}%`, background: d?.color }} />
                    </div>
                  )}
                </div>
              );
            })}
            {/* Fila consolidado */}
            {consolidacion?.length > 0 && (() => {
              const totalIngresos = consolidacion?.reduce((s, d) => s + (d?.ingresos_facturados || 0), 0);
              const totalCostes = consolidacion?.reduce((s, d) => s + (d?.costes_fijos || 0) + (d?.costes_variables || 0), 0);
              const totalBeneficio = consolidacion?.reduce((s, d) => s + (d?.beneficio_operativo || 0), 0);
              const totalCostesVar = consolidacion?.reduce((s, d) => s + (d?.costes_variables || 0), 0);
              const totalMargen = totalIngresos > 0 ? ((totalIngresos - totalCostesVar) / totalIngresos) * 100 : 0;
              return (
                <div className="rounded-xl p-3 mt-1" style={{ background: 'rgba(214,239,78,0.04)', border: '1px solid rgba(214,239,78,0.15)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">📊</span>
                      <span className="font-oranienbaum text-[#D6EF4E] text-xs font-bold">CONSOLIDADO</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'Ingresos', value: totalIngresos, color: '#34D399' },
                      { label: 'Costes', value: totalCostes, color: '#F87171' },
                      { label: 'Resultado', value: totalBeneficio, color: totalBeneficio >= 0 ? '#34D399' : '#F87171' },
                      { label: 'Margen', pct: totalMargen, color: '#D6EF4E' },
                    ]?.map((metric, j) => (
                      <div key={j} className="text-center">
                        <p className="font-oranienbaum text-white/25 text-[9px] uppercase tracking-wider">{metric?.label}</p>
                        <p className="font-oranienbaum text-xs font-bold mt-0.5" style={{ color: metric?.color }}>
                          {metric?.pct !== undefined ? fmtPct(metric?.pct) : fmtEur(metric?.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanzasCorporativas;
